import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const AdminMergeStudents = () => {
  const { studentId1, studentId2 } = useParams();
  const navigate = useNavigate();
  const [comparison, setComparison] = useState(null);
  const [relationships, setRelationships] = useState(null);
  const [fieldSelections, setFieldSelections] = useState({});
  const [keepStudentId, setKeepStudentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchComparison();
  }, [studentId1, studentId2]);

  const fetchComparison = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

      const response = await fetch(
        `${API_URL}/api/admin/compare-students/${studentId1}/${studentId2}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch comparison');
      }

      const data = await response.json();
      setComparison(data.comparison);
      setRelationships(data.relationships);

      // Initialize field selections based on recommendations
      const initialSelections = {};
      Object.values(data.comparison).forEach(group => {
        group.forEach(field => {
          if (field.recommended === 1) {
            initialSelections[field.field] = 'keep';
          } else if (field.recommended === 2) {
            initialSelections[field.field] = 'delete';
          } else {
            initialSelections[field.field] = 'keep'; // Default to keep
          }
        });
      });
      setFieldSelections(initialSelections);

      // Determine which student to keep based on relationships and verified data
      const student1RelCount =
        data.relationships.student1.applied_history_count +
        data.relationships.student1.interactions_count;
      const student2RelCount =
        data.relationships.student2.applied_history_count +
        data.relationships.student2.interactions_count;

      setKeepStudentId(student1RelCount >= student2RelCount ? parseInt(studentId1) : parseInt(studentId2));

    } catch (err) {
      console.error('Error fetching comparison:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMerge = async () => {
    if (!window.confirm(
      `Are you sure you want to merge these records? This action cannot be undone.\n\n` +
      `Keeping: Student #${keepStudentId}\n` +
      `Deleting: Student #${keepStudentId === parseInt(studentId1) ? studentId2 : studentId1}`
    )) {
      return;
    }

    try {
      setMerging(true);
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

      const deleteStudentId = keepStudentId === parseInt(studentId1) ? parseInt(studentId2) : parseInt(studentId1);

      const response = await fetch(`${API_URL}/api/admin/merge-students`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          keep_student_id: keepStudentId,
          delete_student_id: deleteStudentId,
          field_selections: fieldSelections
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Merge failed');
      }

      alert('Records merged successfully!');
      navigate('/admin/duplicate-students');

    } catch (err) {
      console.error('Error merging records:', err);
      alert(`Merge failed: ${err.message}`);
    } finally {
      setMerging(false);
    }
  };

  const displayValue = (value) => {
    if (value === null || value === undefined) return <span className="text-gray-400">Empty</span>;
    if (value === '' || value === '0') return <span className="text-gray-400">Placeholder</span>;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'string' && value.length > 50) {
      return <span className="text-xs">{value.substring(0, 50)}...</span>;
    }
    return value;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading comparison...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
            <button
              onClick={() => navigate('/admin/duplicate-students')}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Back to Duplicates
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <button
            onClick={() => navigate('/admin/duplicate-students')}
            className="mb-4 text-sm text-gray-600 hover:text-gray-900 inline-flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Duplicates
          </button>

          <h1 className="text-3xl font-bold text-gray-900">
            Merge Student Records
          </h1>
          <p className="text-gray-600 mt-2">
            Compare and merge Student #{studentId1} and Student #{studentId2}
          </p>
        </div>

        {/* Record Selection */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Which record should we keep?
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setKeepStudentId(parseInt(studentId1))}
              className={`p-4 rounded-lg border-2 transition-all ${
                keepStudentId === parseInt(studentId1)
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="font-semibold text-lg">Student #{studentId1}</div>
              <div className="text-sm text-gray-600 mt-2">
                {relationships.student1.applied_history_count} applied history •
                {relationships.student1.interactions_count} interactions
              </div>
            </button>

            <button
              onClick={() => setKeepStudentId(parseInt(studentId2))}
              className={`p-4 rounded-lg border-2 transition-all ${
                keepStudentId === parseInt(studentId2)
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <div className="font-semibold text-lg">Student #{studentId2}</div>
              <div className="text-sm text-gray-600 mt-2">
                {relationships.student2.applied_history_count} applied history •
                {relationships.student2.interactions_count} interactions
              </div>
            </button>
          </div>
        </div>

        {/* Field-by-Field Comparison */}
        {Object.entries(comparison).map(([groupName, fields]) => {
          // Only show groups that have differences
          const hasDifferences = fields.some(field => field.different);
          if (!hasDifferences) return null;

          return (
            <div key={groupName} className="bg-white rounded-lg shadow mb-6">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900 capitalize">
                  {groupName.replace(/_/g, ' ')}
                </h3>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/5">
                        Field
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-2/5">
                        Student #{studentId1}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-2/5">
                        Student #{studentId2}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {fields.map(field => (
                      <tr key={field.field} className={field.different ? 'bg-yellow-50' : ''}>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {field.field.replace(/_/g, ' ')}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="flex items-center justify-between">
                            <div className="flex-grow">{displayValue(field.value1)}</div>
                            {field.different && (
                              <input
                                type="radio"
                                name={field.field}
                                checked={fieldSelections[field.field] === 'keep'}
                                onChange={() => setFieldSelections({
                                  ...fieldSelections,
                                  [field.field]: 'keep'
                                })}
                                className="ml-4"
                              />
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="flex items-center justify-between">
                            <div className="flex-grow">{displayValue(field.value2)}</div>
                            {field.different && (
                              <input
                                type="radio"
                                name={field.field}
                                checked={fieldSelections[field.field] === 'delete'}
                                onChange={() => setFieldSelections({
                                  ...fieldSelections,
                                  [field.field]: 'delete'
                                })}
                                className="ml-4"
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}

        {/* Merge Button */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Ready to merge?
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                This will permanently delete Student #{keepStudentId === parseInt(studentId1) ? studentId2 : studentId1}
                {' '}and transfer all relationships to Student #{keepStudentId}.
              </p>
            </div>
            <button
              onClick={handleMerge}
              disabled={merging}
              className="px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {merging ? 'Merging...' : 'Merge Records'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMergeStudents;
